import { Field, Form, Formik } from 'formik'
import React from 'react'

import { Dashboard, DashboardBackButton, PageLayout } from '@components'
import { LeavingGuard } from '@components/utils/LeavingGuard'
import { SECTION_NAMES } from '@constants/sectionNames'
import {
  Accordion,
  Banner,
  BaseContent,
  Button,
  ButtonClean,
  Checkbox,
  EntityTableCell,
  Fieldset,
  HeadContent,
  List,
  Loader,
  Select,
  Subsection,
  Tag,
  useModalContext,
} from '@fourthwall/components'
import { routing } from '@utils/routing'

import { ConfirmBar } from '../../../../modules/ConfirmBar'
import { signets } from '../../assets/signets'
import { AccordionHeading } from '../../components/AccordionHeading/AccordionHeading'
import { ConnectBar } from '../../components/ConnectBar/ConnectBar'
import { ProductsStatusModal } from '../../components/ProductsStatusModal/ProductsStatusModal'
import { EmptyProductsApprovalStatus } from '../../modules/EmptyProductsApprovalStatus'
import { ConfigureSchema } from './AppsMeta.consts'
import * as S from './AppsMeta.styled'
import type { ConfigureValues, ProductItems, SettingsValues } from './AppsMeta.types'
import { CheckoutUrlSetupModal } from './components/CheckoutUrlSetupModal/CheckoutUrlSetupModal'
import { useAppsMeta } from './hooks/useAppsMeta'

export const AppsMeta = () => {
  const { statusTagProps, connectBarProps, settings, products, configure, checkoutUrls } =
    useAppsMeta()
  const { open } = useModalContext()

  const renderConfigureContent = () => {
    if (!configure) return null

    const { type } = configure

    if (type === 'CONNECT_FIRST') {
      return <BaseContent>Connect your Meta account first.</BaseContent>
    }

    if (type === 'CREATE_SHOP') {
      return (
        <>
          <BaseContent>
            Your Meta account doesn’t have any shops created. Refresh this page after adding a Meta
            Shop to your account.
          </BaseContent>
          <S.ActionContainer>
            <Button
              appearance="primary"
              label="Create a new Meta Shop"
              href={configure.url}
              target="_blank"
            />
          </S.ActionContainer>
        </>
      )
    }

    if (type === 'SELECT_SHOP') {
      return (
        <Formik<ConfigureValues>
          initialValues={configure.initialValues || { metaCatalogId: '' }}
          validationSchema={ConfigureSchema}
          validateOnMount
          enableReinitialize
          onSubmit={configure.onSubmit}
        >
          {(formik) => (
            <Form>
              <Fieldset>
                <Fieldset.Row>
                  <Fieldset.Item>
                    <Field
                      component={Select}
                      name="metaCatalogId"
                      label="Select your Meta Product Catalog"
                      suggestion={
                        <>
                          Pick your Meta Product Catalog or{' '}
                          <a href={configure.url} target="_blank" rel="noreferrer">
                            create a new shop
                          </a>
                          .
                        </>
                      }
                      {...configure.selectProps}
                    />
                  </Fieldset.Item>
                </Fieldset.Row>
                {configure.previewProps && (
                  <S.ConfigurePreviewContainer>
                    <List
                      data={[configure.previewProps]}
                      renderItem={(item: typeof configure.previewProps) => (
                        <S.ConfigurePreviewContent>
                          <div>
                            {configure.isSelectedCatalogUsedInAnotherShop && (
                              <Banner appearance="danger" size="small">
                                This catalog is used in another shop
                              </Banner>
                            )}
                            <EntityTableCell
                              top={item.catalogTitle}
                              bottom={
                                <>
                                  <div>{item.catalogDescription}</div>
                                  <BaseContent size="xsmall" appearance="secondary">
                                    {item.catalogFooter}
                                  </BaseContent>
                                </>
                              }
                            />
                          </div>
                        </S.ConfigurePreviewContent>
                      )}
                    />
                  </S.ConfigurePreviewContainer>
                )}
                <Fieldset.Row>
                  <Fieldset.Item>
                    <Button
                      type="submit"
                      label="Confirm selection"
                      appearance="primary"
                      disabled={!formik.isValid || configure.isSelectedCatalogUsedInAnotherShop}
                      loading={formik.isSubmitting}
                    />
                  </Fieldset.Item>
                </Fieldset.Row>
              </Fieldset>
            </Form>
          )}
        </Formik>
      )
    }
  }

  const renderSettings = () => {
    if (!settings) return null

    return (
      <Formik<SettingsValues>
        initialValues={settings.initialValues}
        enableReinitialize
        onSubmit={settings.onSubmit}
      >
        {(formik) => (
          <Subsection title="Settings">
            {settings.banners.length > 0 && (
              <S.BannersContainer>
                {settings.banners.map((banner, index) => (
                  <Banner key={index} {...banner} />
                ))}
              </S.BannersContainer>
            )}
            <Form>
              <Fieldset>
                <Fieldset.Row>
                  <Fieldset.Item>
                    <Field
                      component={Checkbox}
                      name="status"
                      disabled={settings.disabled}
                      label="Enable sales on Instagram/Facebook shop"
                      description="Turning this on will allow fans to purchase your Fourthwall products directly from your Instagram or Facebook shop."
                    />
                  </Fieldset.Item>
                </Fieldset.Row>
              </Fieldset>
            </Form>
            <ConfirmBar
              isOpen={formik.dirty}
              isLoading={formik.isSubmitting}
              onConfirm={formik.submitForm}
              onCancel={formik.resetForm}
            />
            <LeavingGuard when={formik.dirty} isValid={formik.isValid} onSave={formik.submitForm} />
          </Subsection>
        )}
      </Formik>
    )
  }

  const renderMetaCheckoutUrl = () => {
    // Only show if we have the checkout URLs available
    if (!checkoutUrls) return null

    return (
      <Subsection title="Meta checkout URL">
        <BaseContent>
          <p>
            If you haven't configured your Meta checkout URL you can start the process by clicking
            on a button below:
          </p>
        </BaseContent>
        <S.ActionContainer>
          <Button
            appearance="secondary"
            label="Configure Meta checkout URL"
            onClick={() => open(CheckoutUrlSetupModal, checkoutUrls)}
          />
        </S.ActionContainer>
      </Subsection>
    )
  }

  const renderProducts = () => {
    if (!products) return null

    if (products.type === 'LOADING') {
      return (
        <Subsection title="Product sync status">
          <S.LoaderContainer>
            <Loader size="large" />
          </S.LoaderContainer>
        </Subsection>
      )
    }

    if (products.type === 'EMPTY') {
      return <EmptyProductsApprovalStatus appName="Meta" />
    }

    const handleSeeDetails = (title: string, products: ProductItems) => {
      open(ProductsStatusModal, { title, products })
    }

    return (
      <Subsection title="Product sync status">
        <BaseContent>
          <p>
            Products that meet Meta's requirements are synced to your Instagram or Facebook shop.
            This process can take up to an hour.
          </p>
        </BaseContent>
        <S.ProductStatusAccordion>
          <Accordion>
            {products.groups.map((group) => (
              <Accordion.Item
                key={group.type}
                heading={
                  <AccordionHeading
                    status={group.type}
                    label={group.label}
                    onSeeDetails={() => {
                      if (group.details.type === 'ITEMS') {
                        return handleSeeDetails(group.label, group.details.items)
                      }

                      if (group.details.type === 'LINK') {
                        return window.open(group.details.url, '_blank')
                      }
                    }}
                  />
                }
              />
            ))}
          </Accordion>
        </S.ProductStatusAccordion>
      </Subsection>
    )
  }

  return (
    <>
      <HeadContent title={SECTION_NAMES.appMeta} />
      <PageLayout size="tiny">
        <Dashboard.Section
          name={SECTION_NAMES.appMeta}
          topContent={<DashboardBackButton label={SECTION_NAMES.apps} to={routing.apps.self} />}
        >
          <Subsection>
            <S.SubsectionContent>
              <div>
                {statusTagProps && <Tag {...statusTagProps} size="large" display="inline-flex" />}
              </div>
              <div>
                <BaseContent>
                  Showcase and sell products directly to your fans on Instagram or Facebook.{' '}
                  <ButtonClean
                    label="Learn More"
                    href={routing.external.helpCenter.meta}
                    target="_blank"
                  />
                </BaseContent>
              </div>
              {connectBarProps && <ConnectBar {...connectBarProps} logoSrc={signets.metaSignet} />}
            </S.SubsectionContent>
          </Subsection>
          {configure && (
            <Subsection title="Configure your Instagram or Facebook Shop">
              {renderConfigureContent()}
            </Subsection>
          )}
          {renderSettings()}
          {renderMetaCheckoutUrl()}
          {renderProducts()}
        </Dashboard.Section>
      </PageLayout>
    </>
  )
}
